import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Axios, { AxiosStrapi, LOCAL_STORAGE_NAME } from "../axiosInstance";
import { apiEndpoints, strapiApiEndPoints } from "../../api";

const USER_POLICIES_STORAGE_KEY = "cachedUserPolicies";

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_NAME);
    const info = raw ? JSON.parse(raw) : null;
    return info?.userName || info?.Cpr || info?.cpr || null;
  } catch (e) {
    return null;
  }
};

const loadCachedUserPolicies = () => {
  try {
    const raw = localStorage.getItem(USER_POLICIES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.policies)) return null;
    const currentUser = getCurrentUserId();
    if (!currentUser || parsed.owner !== currentUser) return null;
    return parsed.policies;
  } catch (e) {
    return null;
  }
};

const saveCachedUserPolicies = (policies) => {
  try {
    const owner = getCurrentUserId();
    if (policies && Array.isArray(policies) && owner) {
      localStorage.setItem(
        USER_POLICIES_STORAGE_KEY,
        JSON.stringify({ owner, policies })
      );
    } else {
      localStorage.removeItem(USER_POLICIES_STORAGE_KEY);
    }
  } catch (e) {
    // ignore storage errors (quota, private mode, etc.)
  }
};

const initialState = {
  policies_bg: null,
  isLoading: false,
  error: null,

  userPolicies: loadCachedUserPolicies(),
  userPoliciesOwner: getCurrentUserId(),
  isLoadingUserPolicies: false,
  // Whether a policies request has come back at least once this session.
  // `isLoadingUserPolicies` alone cannot answer "does this user have no
  // policies": it is false before the first request starts as well as after
  // the last one finishes, so an empty state gated on it flashes on mount
  // for every user, including those who do have policies.
  hasFetchedUserPolicies: false,
  errorUserPolicies: null,
};

export const fetchPoliciesBg = createAsyncThunk(
  "fetchPoliciesBg",
  (queryString, { rejectWithValue }) => {
    return AxiosStrapi.get(`${strapiApiEndPoints.policiesBg}`).then((res) => res.data);
  }
);
export const fetchUserPolicies = createAsyncThunk(
  "fetchUserPolicies",
  (paylaod, { rejectWithValue }) => {
    const { token, ...requestPayload } = paylaod;
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return Axios.post(`${apiEndpoints.getPolicies}`, requestPayload, config).then((res) => res.data);
  }
);

export const myPolicySlice = createSlice({
  name: "offers slice",
  initialState,
  reducers: {
    clearOffersError: (state) => {
      state.error = null;
    },
    clearUserPoliciesError: (state) => {
      state.errorUserPolicies = null;
    },
    clearUserPolicies: (state) => {
      state.userPolicies = null;
      state.userPoliciesOwner = null;
      state.hasFetchedUserPolicies = false;
      try { localStorage.removeItem(USER_POLICIES_STORAGE_KEY); } catch (e) {}
    },
  },
  extraReducers: (builder) => {
    // FETCH  UTILS API
    builder.addCase(fetchPoliciesBg.pending, (state, action) => {
      if (!state.policies_bg) {
        state.isLoading = true;
      }
    });
    builder.addCase(fetchPoliciesBg.fulfilled, (state, action) => {
      state.isLoading = false;
      state.policies_bg = action.payload?.data;
    });
    builder.addCase(fetchPoliciesBg.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error?.message;
    });
    // FETCH  USER POLICIES API
    builder.addCase(fetchUserPolicies.pending, (state, action) => {
      state.isLoadingUserPolicies = true;
      // Cross-user safety: if the logged-in user changed since the cache was
      // populated, drop stale policies immediately so user B never sees user A's
      // data during the refresh window. Same user: keep cached data visible.
      const currentUser = getCurrentUserId();
      if (!currentUser || state.userPoliciesOwner !== currentUser) {
        state.userPolicies = null;
        state.userPoliciesOwner = currentUser;
      }
    });
    builder.addCase(fetchUserPolicies.fulfilled, (state, action) => {
      state.isLoadingUserPolicies = false;
      state.hasFetchedUserPolicies = true;
      state.userPolicies = action.payload;
      state.userPoliciesOwner = getCurrentUserId();
      saveCachedUserPolicies(action.payload);
    });
    builder.addCase(fetchUserPolicies.rejected, (state, action) => {
      state.isLoadingUserPolicies = false;
      // A failed request has still settled. Treating it as unsettled would
      // leave the policies area permanently blank when the API is down —
      // no cards, no skeleton, and no message either.
      state.hasFetchedUserPolicies = true;
      // state.error = action.error?.message;
    });
  },
});

// SELECTORS
export const selectPoliciesBgData = (store) => store?.myPolicies?.policies_bg;
export const selectMyPoliciesLoader = (store) => store?.myPolicies?.isLoading;

export const selectUserPoliciesData = (store) => store?.myPolicies?.userPolicies;
export const selectUserPoliciesLoader = (store) => store?.myPolicies?.isLoadingUserPolicies;
export const selectHasFetchedUserPolicies = (store) =>
  store?.myPolicies?.hasFetchedUserPolicies;

export const myPoliciesWholeLoader = (store) => store?.myPolicies?.isLoading || store?.myPolicies?.isLoadingUserPolicies

// ACTIONS TO DISPATCH
export const { clearOffersError, clearUserPoliciesError, clearUserPolicies } = myPolicySlice.actions;

// Alias: same thunk, more natural name for callers that just want
// the GetPolicies API (e.g. Quick Renewal).
export { fetchUserPolicies as getPolicies };

export default myPolicySlice.reducer;
